// deps
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// external-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { makeApiCall } from "@swiggy-private/core-utils";
import {
    CheckoutContext,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    DineoutCartContext,
    getSwiggyHeaders,
} from "@swiggy-private/checkout-core";

// contexts
import { DineoutSharedCartContext } from "@dineout-cart-client/contexts";

// interfaces
import {
    DineCashPollingRequest,
    DineCashPollingResponseData,
    UseDineCashPollingOptions,
} from "@dineout-cart-client/interfaces/DineCashPolling";

// constants
import {
    DINE_CASH_POOLING_ELIGIBLE_CART_STATES,
    DOWNSTREAM_ENDPOINTS,
    DOWNSTREAM_HOSTS,
    PoolingMetricsSource,
} from "@dineout-cart-client/constants";

const POLLING_INTERVAL_MS = 5000;

export const useDineCashPolling = (options?: UseDineCashPollingOptions) => {
    const { onDineCashPoolUpdated } = options || {};
    const { checkoutState } = useContext(CheckoutContext);
    const { cartState } = useContext(DineoutCartContext);
    const rewardsPoolWidget = cartState?.reviewedCart?.data?.rewardsPoolWidget;
    const {
        maxPoolAmountAllowed,
        maxNoOfContributorsAllowed,
        maxPerPersonCap,
        poolType,
        userIntent,
        setUserIntent,
        lastKnownUpdatedAt,
        setLastKnownUpdatedAt,
    } = useContext(DineoutSharedCartContext);
    const cartId = cartState?.reviewedCart?.data?.cartId || "";

    const [pollingData, setPollingData] = useState<DineCashPollingResponseData | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Seed from shared context so that on back-navigation from the pool page,
    // the first cart-page poll can detect contributions that arrived during the pool session.
    const lastUpdatedAtRef = useRef<string | null>(lastKnownUpdatedAt);
    const onPoolUpdatedRef = useRef(onDineCashPoolUpdated);
    onPoolUpdatedRef.current = onDineCashPoolUpdated;

    const source = options?.source ?? PoolingMetricsSource.CART;

    const currentContributors = useMemo(() => {
        if (source === PoolingMetricsSource.POOL_PAGE) {
            return pollingData?.senders?.length ?? 0;
        }
        return rewardsPoolWidget?.noOfCurrentContributors ?? 0;
    }, [source, pollingData?.senders?.length, rewardsPoolWidget?.noOfCurrentContributors]);

    const currentContribution = useMemo(() => {
        if (source === PoolingMetricsSource.POOL_PAGE) {
            return pollingData?.pool?.totalAmountReceived ?? 0;
        }
        return rewardsPoolWidget?.totalPledgedAmount ?? 0;
    }, [source, pollingData?.pool?.totalAmountReceived, rewardsPoolWidget?.totalPledgedAmount]);

    const isMaxReached = useMemo(() => {
        const contributorsMaxed = maxNoOfContributorsAllowed > 0 && currentContributors >= maxNoOfContributorsAllowed;
        const contributionMaxed = maxPoolAmountAllowed > 0 && currentContribution >= maxPoolAmountAllowed;
        return contributorsMaxed || contributionMaxed;
    }, [currentContributors, currentContribution, maxNoOfContributorsAllowed, maxPoolAmountAllowed]);

    const shouldPoll = useMemo(() => {
        if (!DINE_CASH_POOLING_ELIGIBLE_CART_STATES.includes(checkoutState.type)) return false;
        if (isEmpty(rewardsPoolWidget)) return false;
        if (!userIntent) return false;
        if (!cartId) return false;
        return !isMaxReached;
    }, [checkoutState.type, rewardsPoolWidget, userIntent, isMaxReached, cartId]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const fetchPollingData = useCallback(async () => {
        const headers = GlobalHeaderState.getHeaders();
        const userId = headers[SwgyHeaderKeysList.userId] || "";
        const deviceId = headers[SwgyHeaderKeysList.deviceId] || "";

        if (!userId || !cartId || !deviceId) {
            return;
        }

        const requestBody: DineCashPollingRequest = {
            receiver: { userId, deviceId, cartId },
            pool: {
                poolType,
                maxPerPersonCap,
                maxPoolAmountAllowed,
                maxNoOfContributor: maxNoOfContributorsAllowed,
            },
        };

        try {
            const response = await makeApiCall<DineCashPollingResponseData>({
                apiHost: DOWNSTREAM_HOSTS.GROWTHX_API_HOST,
                endpoint: DOWNSTREAM_ENDPOINTS.GROWTHX_POLLING,
                options: {
                    method: "POST",
                    headers: { ...getSwiggyHeaders(headers), "client-id": "portal" },
                },
                bodyParams: requestBody as unknown as Record<string, unknown>,
                retry: false,
            });

            if (response?.statusCode === 0 && !isEmpty(response?.data)) {
                const data = response.data;
                setPollingData(data);

                const newLastUpdatedAt = data.lastUpdatedAt;
                if (lastUpdatedAtRef.current !== null) {
                    // Normal change detection: lastUpdatedAt changed → pool was updated.
                    if (newLastUpdatedAt !== lastUpdatedAtRef.current) {
                        onPoolUpdatedRef.current?.();
                    }
                } else if (source === PoolingMetricsSource.POOL_PAGE) {
                    // First poll on pool page with no prior baseline (lastUpdatedAtRef = null).
                    // Cannot use timestamp diff — instead check if actual contributions exist.
                    // This covers contributions that arrived before the first poll fires.
                    if ((data.pool?.totalAmountReceived ?? 0) > 0) {
                        onPoolUpdatedRef.current?.();
                    }
                    // For CART source with null baseline: preserve existing behaviour —
                    // do not call onPoolUpdatedRef on first poll to avoid a duplicate
                    // updateCart on initial cart page load.
                }
                lastUpdatedAtRef.current = newLastUpdatedAt;
                // Keep shared context in sync so subsequent hook instances (e.g. on
                // back-nav to cart page) can seed their lastUpdatedAtRef correctly.
                setLastKnownUpdatedAt(newLastUpdatedAt);
            }
        } catch {
            /* no-op */
        }
    }, [
        cartId,
        poolType,
        maxPerPersonCap,
        maxPoolAmountAllowed,
        maxNoOfContributorsAllowed,
        setLastKnownUpdatedAt,
        source,
    ]);

    const fetchPollingDataRef = useRef(fetchPollingData);
    fetchPollingDataRef.current = fetchPollingData;

    const startPolling = useCallback(() => {
        if (intervalRef.current || isMaxReached) return;
        setUserIntent(true);
        fetchPollingDataRef.current();
        intervalRef.current = setInterval(() => fetchPollingDataRef.current(), POLLING_INTERVAL_MS);
    }, [setUserIntent, isMaxReached]);

    useEffect(() => {
        if (isMaxReached) {
            stopPolling();
        }
    }, [isMaxReached, stopPolling]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopPolling();
            } else if (shouldPoll) {
                startPolling();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [stopPolling, startPolling, shouldPoll]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    return { pollingData, startPolling, stopPolling, shouldPoll };
};
